import { Button, Col, Form, Input, message, Radio, Row, Space, Typography } from "antd";
import { Icon } from "@iconify/react";
import styles from "@modules/ContractManagement/SPA/SPA.module.scss";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import React, { ReactNode, useEffect, useState } from "react";
import { ContentWrapper } from "~/Components";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { allowedDomainsAll } from "~/Utils";
import { get } from "@services/api.service";
import { post } from "~/Services";
import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { useNotification } from "~/Hooks/useNotification";

interface FormItems {
  userName: string;
  userEmail: string;
  role: string | null;
}

const defaultValue = {
  userName: "",
  userEmail: "",
  role: "",
} as FormItems;

const EqualCol = ({ children }: { children: ReactNode }) => (
  <Col
    xs={24}
    md={12}
  >
    {children}
  </Col>
);

export const NewUser = () => {
  const [loading, setLoading] = useState(false);
  const [successLoader, setSuccessLoader] = useState(false);
  const navigate = useNavigate();
  const { openToast,openNotification } = useNotification();
  

  const validationSchema = yup.object().shape({
    userName: yup
      .string()
      .trim("Name cannot include leading and trailing spaces")
      .strict(true)
      .matches(/^[aA-zZ\s]+$/, "Invalid Name")
      .required("Name is required"),
    userEmail: yup
      .string()
      .required("Email required")
      .lowercase("Email address must be lowercase")
      .email("Email address must be a valid email")
      .strict(true)
      .matches(
        new RegExp(`^[a-zA-Z0-9._%+-]+@(${allowedDomainsAll.join("|")})$`),
        `Permitted Domains: ${allowedDomainsAll.join(", ")}`
      ),
    role: yup.string().nullable().required("Role is required"),
  });

  const onSubmit = async (values: any) => {
    setLoading(true);
    const payload = {
      name: values.userName,
      email: values.userEmail,
      status: "active",
      requestRoles: [values.role],
    };
    post(`/v1/user/onboarded/add-user`, payload)
      .then((res) => {
        setSuccessLoader(true);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const [checkMail, setCheckMail] = useState<any>();

  useEffect(() => {
    if (checkMail) {
      get(`v1/user/is-valid-email?userEmail=${checkMail}`)
        .then((res: any) => {
          if(res){
            // openNotification({
            //   message: "User is present in Organization",
            //   type: "success",
            // });
          }
        })
        .catch(() => {
          // openToast({
          //   content: "User not present in Organization",
          //   type: "error",
          // });
        });
    }
  }, [checkMail]);

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate(-1)}
            icon="akar-icons:arrow-left"
            style={{
              fontSize: "1.8rem",
              cursor: "pointer",
              color: styles.primary,
              marginRight: styles.whitespace1,
            }}
          />
          <span>
            <Typography.Title
              level={3}
              style={{ margin: 0 }}
            >
              Add New User
            </Typography.Title>
          </span>
        </Space>
      }
    >
      <Formik
        initialValues={defaultValue}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validationSchema}
      >
        {(props) => {
          useEffect(() => {
            const email = props.values.userEmail;
            if (email && props.errors) {
              const domain = email.split("@")[1];

              if (allowedDomainsAll.includes(domain)) {
                setCheckMail(email);
              }
            }
          }, [props.values.userEmail, props.errors]);

          return (
            <Form
              layout="vertical"
              onFinish={props.handleSubmit}
            >
              <Row style={{ marginTop: 16, display: "block", width: "100%" }}>
                <Col
                  xs={24}
                  md={16}
                >
                  <>
                    <Row gutter={16}>
                      <EqualCol>
                        <Form.Item label="User Name">
                          <Field name="userName">{({ field }: FieldProps) => <Input {...field} />}</Field>
                          <Typography.Text type="danger">
                            <ErrorMessage name="userName" />
                          </Typography.Text>
                        </Form.Item>
                      </EqualCol>
                      <EqualCol>
                        <Form.Item label="User Email">
                          <Field name="userEmail">{({ field }: FieldProps) => <Input {...field} />}</Field>
                          <Typography.Text type="danger">
                            <ErrorMessage name="userEmail" />
                          </Typography.Text>
                        </Form.Item>
                      </EqualCol>
                    </Row>
                    <Row gutter={16}>
                      <Col md={24}>
                        <Typography.Title
                          level={5}
                          style={{ marginBottom: 10 }}
                        >
                          Choose the access you want
                        </Typography.Title>
                      </Col>
                      <Col md={24}>
                        <Form.Item
                          name="role"
                          initialValue=""
                        >
                          <Field name="role">
                            {({ field }: FieldProps) => (
                              <Radio.Group {...field}>
                                <Radio value="SPA_APPROVER">SPA Approver</Radio>
                                <Radio value="SPA_ADMIN">SPA Admin</Radio>
                              </Radio.Group>
                            )}
                          </Field>
                        </Form.Item>
                        <Typography.Text type="danger">
                          <ErrorMessage name="role" />
                        </Typography.Text>
                      </Col>
                    </Row>
                  </>
                </Col>
              </Row>
              <Row>
                <Col
                  xs={16}
                  md={16}
                  style={{ textAlign: "right" }}
                >
                  <Space>
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={loading}
                      disabled={!props.isValid}
                    >
                      Submit
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
      <SuccessModal
        redirect={-1}
        title="You have successfully created a new user"
        message=""
        type="success"
        buttonText="Go Back"
        open={successLoader}
        setOpen={setSuccessLoader}
      />
    </ContentWrapper>
  );
};
